// Acceptance of Tallerza's Terms of Service and Privacy Policy by a workshop.
// The workshop's admin accepts on behalf of the workshop (the customer) with
// the `aceptarTerminos` callable; the backoffice blocks the admin until the
// stored version is the one in force. Publishing new terms = bumping
// TERMINOS_VERSION here (every admin accepts again) once the texts at the
// URLs below carry that version.
import { z } from 'zod';

/** Version in force of the Terms and the Privacy Policy (published together). */
export const TERMINOS_VERSION = '2026-10';

/**
 * False until /terminos and /privacidad are published at the URLs below: the
 * backoffice gate only runs in dev (emulators, e2e) meanwhile, so a deploy of
 * main does not block admins in front of provisional texts. Flip it (and set
 * TERMINOS_VERSION to the publication date) when the legal texts go live.
 */
export const TERMINOS_PUBLICADOS = false;

export const URL_TERMINOS = 'https://tallerza.cl/terminos';
export const URL_PRIVACIDAD = 'https://tallerza.cl/privacidad';

/** Versions look like "2026-10" (year-month of publication). */
export const versionTerminos = z.string().regex(/^\d{4}-\d{2}$/);

/**
 * `talleres/{t}.terminos`: the last version the workshop accepted. Written
 * only by aceptarTerminos (Admin SDK; firestore.rules deny every client write
 * to the workshop document). Everyone in the workshop reads that document, so
 * this carries no personal data beyond the uid: the evidence (e-mail, IP,
 * user agent) lives in `terminosAceptados/{t}__{version}`, outside the tenant
 * tree, where no client reads or writes.
 */
export const terminosTaller = z.object({
  version: versionTerminos,
  /** Server Timestamp (seconds + nanoseconds in any SDK). */
  aceptadoEn: z.object({ seconds: z.number(), nanoseconds: z.number() }),
  /** uid of the admin who accepted. */
  aceptadoPor: z.string().min(1),
});
export type TerminosTaller = z.infer<typeof terminosTaller>;

/** True when the workshop accepted the version in force. */
export function terminosVigentes(t: { version?: unknown } | null | undefined): boolean {
  return t?.version === TERMINOS_VERSION;
}

/** Id of the evidence record of workshop `t` accepting `version`. */
export const idAceptacionTerminos = (t: string, version: string) => `${t}__${version}`;

/** What aceptarTerminos returns. */
export type RespuestaAceptarTerminos = {
  version: string;
  /** True when this version was already accepted (nothing was written). */
  yaAceptados: boolean;
};

/** Error messages of aceptarTerminos, shared with the backoffice. */
export const MENSAJES_TERMINOS = {
  versionVieja:
    'Los términos cambiaron mientras los leías. Recarga la página para ver la versión vigente.',
} as const;
